// "Report a bug" (sprint 24): the menu bar item on every page opens a short form. Attached by the page itself: page, browser,
// screen size, display scaling, look, time, last round id, recent client errors. Never chat lines: collectDetails reads
// no page text at all, and the server keeps only the fields it knows (server/bugs.js).
export const ERROR_KEEP = 5;
const ERRORS_KEY = 'door.errors', ROUND_KEY = 'door.lastRound';
const WHAT_MAX = 1000, CONTACT_MAX = 120;

export function recordError(list, e) {
  list.push(String(e?.message ?? e?.reason?.message ?? e?.reason ?? e ?? '').slice(0, 200));
  while (list.length > ERROR_KEEP) list.shift();
}

export function collectDetails(env) {
  const scr = env.screen ?? {}, dpr = env.devicePixelRatio ?? 1;
  return {
    page: env.location?.pathname ?? '',
    browser: env.navigator?.userAgent ?? '',
    screen: `${scr.width}x${scr.height} (window ${env.innerWidth}x${env.innerHeight})`,
    scaling: String(dpr),
    look: env.dataset?.look ?? env.look ?? 'one-bit',
    time: new Date(env.now()).toISOString(),
    round: env.round ?? '',
    errors: [...(env.errors ?? [])],
  };
}

const store = {
  get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode: lasts this page only */ } },
};
const errors = [];
let round = '';
export function noteRound(id) { round = String(id ?? ''); store.set(ROUND_KEY, round); }

// A picture of the game screen: every visible canvas on the page, laid over each other where they sit, as one JPEG.
// The one-bit game draws its whole screen (scene, windows, words) on canvases, so this is what the player saw.
// Pages without canvases give no picture.
export function captureScreen(doc = document, maxWidth = 960) {
  const cvs = [...doc.querySelectorAll('canvas')].map(c => [c, c.getBoundingClientRect()]).filter(([c, r]) => r.width > 0 && r.height > 0 && c.width > 0 && getComputedStyle(c).visibility !== 'hidden');
  if (!cvs.length) return null;
  const w = innerWidth, h = innerHeight, k = Math.min(1, maxWidth / w);
  const out = doc.createElement('canvas'); out.width = Math.round(w * k); out.height = Math.round(h * k);
  const g = out.getContext('2d');
  g.fillStyle = '#000'; g.fillRect(0, 0, out.width, out.height); g.imageSmoothingEnabled = false;
  try {
    for (const [c, r] of cvs) g.drawImage(c, r.left * k, r.top * k, r.width * k, r.height * k);
    return out.toDataURL('image/jpeg', 0.75);
  } catch { return null; } // a canvas that cannot be read gives no picture
}

const liveEnv = () => ({
  location, navigator, screen, innerWidth, innerHeight, devicePixelRatio, dataset: document.documentElement.dataset,
  now: Date.now, round: round || store.get(ROUND_KEY) || '', errors,
});

const CSS = `
@font-face{font-family:"Atkinson Hyperlegible";src:url(/fonts/AtkinsonHyperlegible-Regular.woff2) format("woff2");font-weight:400;font-style:normal;font-display:block}
#report-form{position:fixed;inset:0;z-index:1000;display:flex;align-items:flex-start;justify-content:center;overflow:auto;padding:5vh 12px;
  background:#000 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4' shape-rendering='crispEdges'%3E%3Crect width='1' height='1' fill='%23fff'/%3E%3Crect x='2' y='2' width='1' height='1' fill='%23fff'/%3E%3C/svg%3E");
  font:16px/22px "Atkinson Hyperlegible",Verdana,sans-serif;color:#000}
#report-form[hidden]{display:none}
#report-form *{box-sizing:border-box;margin:0;color:inherit;font:inherit}
#report-form .rf-win{width:min(34rem,100%);background:#fff;border:2px solid #000;box-shadow:4px 4px 0 #fff;padding:0 0 16px}
#report-form h2{font:700 20px/28px "Atkinson Hyperlegible",Verdana,sans-serif;padding:2px 12px;border-bottom:2px solid #000;background:repeating-linear-gradient(#000 0 2px,#fff 2px 4px);text-align:center}
#report-form h2 span{background:#fff;padding:0 12px}
#report-form form{display:grid;gap:12px;padding:12px 16px 0}
#report-form p,#report-form label>span,#report-form h2 span{filter:url(#bitb)}
#report-form label{display:grid;gap:4px}
#report-form label>span{font-weight:700;font-size:17px}
#report-form label>span small{font-weight:400;font-size:16px}
#report-form textarea,#report-form input{width:100%;background:transparent;border:2px solid #000;padding:6px 8px;border-radius:0;resize:vertical;filter:url(#bitb)}
#report-form textarea:focus,#report-form input:focus{outline:2px dotted #000;outline-offset:3px}
#report-form .rf-row{display:flex;flex-wrap:wrap;gap:12px}
#report-form button{background:#fff;color:#000;border:2px solid #000;padding:4px 16px;font-weight:700;font-size:17px;line-height:24px;cursor:pointer;box-shadow:0 0 0 2px #fff,0 0 0 4px #000;border-radius:0;min-height:44px}
#report-form button span{filter:url(#bitb)}
#report-form button:hover,#report-form button:focus-visible{background:#000;color:#fff;outline:none}
#report-form button:disabled{cursor:default;opacity:.6}
#report-form .rf-note{font-size:16px}
#report-form .rf-status{min-height:22px;font-weight:700}
#report-form .rf-shot{display:grid;gap:6px}
#report-form .rf-shot[hidden]{display:none}
#report-form .rf-shot input{width:auto;margin-right:6px;vertical-align:middle;filter:none}
#report-form .rf-shot img{max-width:100%;max-height:140px;justify-self:start;border:2px solid #000;image-rendering:pixelated}
html.halloween #report-form .rf-win,body.halloween #report-form .rf-win{border-color:#f08a2c;box-shadow:4px 4px 0 #f08a2c}
html.halloween #report-form h2,body.halloween #report-form h2{background:repeating-linear-gradient(#f08a2c 0 2px,#fff 2px 4px);border-bottom-color:#f08a2c}
html[data-look="colour"] #report-form{background:rgba(7,10,14,.88)}
html[data-look="colour"] #report-form .rf-win{background:#1d2a33;color:#e8e4d6;border-color:#c9a063;box-shadow:none}
html[data-look="colour"] #report-form h2{background:#26363f;border-bottom-color:#c9a063}
html[data-look="colour"] #report-form h2 span{background:transparent}
html[data-look="colour"] #report-form p,html[data-look="colour"] #report-form label>span,html[data-look="colour"] #report-form h2 span,html[data-look="colour"] #report-form textarea,html[data-look="colour"] #report-form input,html[data-look="colour"] #report-form button span{filter:none}
html[data-look="colour"] #report-form textarea,html[data-look="colour"] #report-form input{background:#0f171d;border-color:#c9a063;color:#e8e4d6}
html[data-look="colour"] #report-form button{background:#e8e4d6;color:#12191f;border-color:#12191f;box-shadow:none}
html[data-look="colour"] #report-form button:hover,html[data-look="colour"] #report-form button:focus-visible{background:#d9822b;color:#12191f}
html[data-look="colour"] #report-form textarea:focus,html[data-look="colour"] #report-form input:focus{outline-color:#d9822b}
`;

let box = null, opener = null, shot = null;

function build() {
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.append(style);
  box = document.createElement('div');
  box.id = 'report-form'; box.hidden = true;
  box.innerHTML = `<div class="rf-win" role="dialog" aria-modal="true" aria-labelledby="rf-title">
<h2 id="rf-title"><span>Report a bug</span></h2>
<form id="rf" novalidate>
<p class="rf-note">Tell us what went wrong. The page sends along your page, browser, screen size, display scaling, look, the time, the last round and any recent errors, plus a picture of the game screen if you leave the box ticked. The picture can show your chat.</p>
<label><span>What happened <small>(required)</small></span><textarea id="rf-what" rows="4" maxlength="${WHAT_MAX}" required></textarea></label>
<label><span>What you expected <small>(optional)</small></span><textarea id="rf-expected" rows="2" maxlength="${WHAT_MAX}"></textarea></label>
<label><span>Contact <small>(optional, only if you want an answer)</small></span><input id="rf-contact" maxlength="${CONTACT_MAX}" autocomplete="off"></label>
<label class="rf-shot" id="rf-shot-row"><span><input type="checkbox" id="rf-shot" checked> Attach a picture of the screen</span><img id="rf-shot-img" alt="The picture that will be sent"></label>
<p class="rf-status" id="rf-status" role="status" aria-live="polite"></p>
<div class="rf-row"><button type="submit" id="rf-send"><span>Send report</span></button><button type="button" id="rf-cancel"><span>Cancel</span></button></div>
</form></div>`;
  document.body.append(box);
  const $ = id => box.querySelector('#' + id);
  $('rf-cancel').onclick = close;
  box.addEventListener('pointerdown', e => { if (e.target === box) close(); });
  box.addEventListener('keydown', e => { // the game's own keys (A, D) must not fire while someone types here
    e.stopPropagation();
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    if (e.key === 'Tab') {
      const els = [...box.querySelectorAll('textarea, input, button:not(:disabled)')], first = els[0], last = els.at(-1);
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
  $('rf').onsubmit = async e => {
    e.preventDefault();
    const status = $('rf-status'), what = $('rf-what').value.trim();
    if (!what) { status.textContent = 'Please say what happened.'; $('rf-what').focus(); return; }
    $('rf-send').disabled = true; status.textContent = 'Sending.';
    try {
      const res = await fetch('/bug-report', { method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ what, expected: $('rf-expected').value.trim(), contact: $('rf-contact').value.trim(), ...collectDetails(liveEnv()),
          ...(shot && $('rf-shot').checked ? { shot } : {}) }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'The report could not be sent.');
      status.textContent = 'Thank you. Your report is saved.';
      for (const id of ['rf-what', 'rf-expected', 'rf-contact']) $(id).value = '';
      $('rf-cancel').firstChild.textContent = 'Close';
    } catch (err) { status.textContent = err.message === 'Failed to fetch' ? 'The report could not be sent. Check your connection.' : err.message; }
    $('rf-send').disabled = false;
  };
}

export function openForm() {
  shot = captureScreen(); // before the form covers anything (it is not a canvas, so it never shows in the picture anyway)
  if (!box) build();
  box.querySelector('#rf-shot-row').hidden = !shot;
  if (shot) box.querySelector('#rf-shot-img').src = shot;
  opener = document.activeElement;
  box.querySelector('#rf-status').textContent = '';
  box.querySelector('#rf-cancel').firstChild.textContent = 'Cancel';
  box.hidden = false;
  box.querySelector('#rf-what').focus();
}
function close() { box.hidden = true; opener?.focus?.(); }

if (typeof document !== 'undefined') {
  try { errors.push(...JSON.parse(store.get(ERRORS_KEY) ?? '[]').slice(-ERROR_KEEP)); } catch { /* none kept */ }
  const note = e => { recordError(errors, e); store.set(ERRORS_KEY, JSON.stringify(errors)); };
  addEventListener('error', e => note(e));
  addEventListener('unhandledrejection', e => note(e));
  const hook = () => document.getElementById('report')?.addEventListener('click', e => { e.preventDefault(); openForm(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hook); else hook();
}
